import React, { useState, useEffect } from 'react';
import { useNavigate, useMatch } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ProjectsHeader from '../components/ProjectsHeader';
import NavigationMenu from '../components/NavigationMenu';
import MainContentArea from '../components/MainContentArea';
import PromptsPage from '../pages/PromptsPage';
import ToolsPage from '../pages/ToolsPage';
import SettingsPage from '../pages/SettingsPage';
import AdminSettingsPage from '../pages/AdminSettingsPage';
import SuperAdminSettingsPage from '../pages/SuperAdminSettingsPage';
import SchedulerDashboard from '../pages/SchedulerDashboard';
import SettingsApiKeyTestPage from '../pages/SettingsApiKeyTestPage';
import YumlPage from '../pages/YumlPage';
import '../styles/MainPage.css';
import './SettingsLayout.css';
import { useNotifications } from '../contexts/NotificationsContext';
import { buildMainNavLinks } from '../utils/mainNavLinks';

// Use shared API_BASE_URL from utils (defaults to current origin if env not set)

function SettingsLayout() {
  const { user, loading } = useAuth();
  const { totalCount: horizonBadge, reviewCount, embeddingCount, schedulerErrorCount } = useNotifications();
  const [activeTab, setActiveTab] = useState('prompts');
  const navigate = useNavigate();
  
  // Initialize menu state from cookie
  const getInitialOpen = () => {
    try {
      const match = document.cookie.match(/(?:^|; )nav_open=([^;]*)/);
      if (match) {
        const v = decodeURIComponent(match[1]);
        return v === '1' || v === 'true';
      }
    } catch (_) {}
    return false;
  };
  const [isMenuOpen, setIsMenuOpen] = useState(getInitialOpen);
  const [disableAnim, setDisableAnim] = useState(true);

  useEffect(() => {
    // Enable animations after first paint
    const id = requestAnimationFrame(() => setDisableAnim(false));
    return () => cancelAnimationFrame(id);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen((prev) => {
      const next = !prev;
      try {
        document.cookie = `nav_open=${next ? '1' : '0'}; path=/; max-age=15552000`;
      } catch (_) {}
      return next;
    });
  };

  // Use useMatch to cleanly extract the tab parameter from the URL
  const matchWithWildcard = useMatch('/settings/:tab/*');
  const matchWithoutWildcard = useMatch('/settings/:tab');
  const matchRoot = useMatch('/settings');
  const match = matchWithWildcard || matchWithoutWildcard || matchRoot;
  
  // Sync active tab with URL sub-route under /settings/* and support deep links + back/forward
  useEffect(() => {
    const tab = match?.params?.tab || '';

    // If no sub-route, normalize URL to /settings/ingestion
    if (!tab) {
      navigate('/settings/ingestion', { replace: true });
      setActiveTab('ingestion');
      return;
    }

    // Special handling for superadmin: if we don't yet know the user's role (loading),
    // do NOT redirect away. Let the user stay on /settings/superadmin and wait until
    // AuthContext finishes loading to decide. This avoids premature redirect.
    if (tab === 'superadmin' && loading) {
      if (activeTab !== 'superadmin') setActiveTab('superadmin');
      return;
    }

    // Handle legacy routes
    if (tab === 'settings') {
      navigate('/settings/system', { replace: true });
      setActiveTab('system');
      return;
    }
    if (tab === 'system-settings') {
      navigate('/settings/system', { replace: true });
      setActiveTab('system');
      return;
    }
    if (tab === 'user-settings') {
      navigate('/settings/user', { replace: true });
      setActiveTab('user');
      return;
    }
    // Handle legacy Horizon routes - redirect to Horizon
    if (tab === 'main' || tab === 'data-review' || tab === 'data-management') {
      navigate(`/horizon/${tab}`, { replace: true });
      return;
    }

    if (tab === 'ai-tests' || tab === 'system-tests') {
      navigate('/help/system-tests', { replace: true });
      return;
    }

    // Deprecated periodic-ingestion Schedules UI → toolchain Scheduler
    if (tab === 'schedules') {
      navigate('/settings/scheduler', { replace: true });
      setActiveTab('scheduler');
      return;
    }

    const allowedTabs = ['ingestion', 'scheduler', 'prompts', 'tools', 'views', 'yuml', 'system', 'user', 'key-test'];
    if (user?.accountType === 'admin' || user?.accountType === 'superadmin') {
      allowedTabs.push('admin');
    }
    if (user?.accountType === 'superadmin') {
      allowedTabs.push('superadmin');
    }

    if (!allowedTabs.includes(tab)) {
      // Invalid or unauthorized tab; redirect to ingestion
      navigate('/settings/ingestion', { replace: true });
      setActiveTab('ingestion');
      return;
    }

    if (tab !== activeTab) {
      setActiveTab(tab);
    }
  }, [match, user, loading, navigate, activeTab]);

  const renderContent = () => {
    switch (activeTab) {
      case 'prompts':
        return <PromptsPage />;
      case 'tools':
        return <ToolsPage />;
      case 'system':
        return <SettingsPage initialTab="system" />;
      case 'user':
        return <SettingsPage initialTab="user" />;
      case 'key-test':
        return <SettingsApiKeyTestPage />;
      case 'ingestion':
        return <SettingsPage initialTab="ingestion" />;
      case 'scheduler':
        return <SchedulerDashboard />;
      case 'views':
        return <SettingsPage initialTab="views" />;
      case 'yuml':
        return <YumlPage />;
      case 'admin':
        return <AdminSettingsPage />;
      case 'superadmin':
        return <SuperAdminSettingsPage />;
      default:
        return <PromptsPage />;
    }
  };

  const linkItems = buildMainNavLinks({
    user,
    horizonBadge,
    embeddingCount,
    reviewCount,
    schedulerErrorCount,
  });

  return (
    <div className="settings-layout">
      <ProjectsHeader
        isMenuOpen={isMenuOpen}
        onToggle={toggleMenu}
        title="Settings"
      />
      <div className="settings-container">
        <NavigationMenu
          isOpen={isMenuOpen}
          onToggle={toggleMenu}
          disableTransition={disableAnim}
          linkItems={linkItems}
          menuItems={[]}
          activeItem={activeTab}
          onItemClick={(id) => {
            setActiveTab(id);
            navigate(`/settings/${id}`);
          }}
          showUserMenu={true}
        />
        <MainContentArea isMenuOpen={isMenuOpen} disableTransition={disableAnim}>
          <div className="settings-main-content">
            {renderContent()}
          </div>
        </MainContentArea>
      </div>
    </div>
  );
}

export default SettingsLayout;
